/**
 * @typedef {Object} RingpoolSettings
 * @property {String} pageType - the `customfield1` value on the page (or empty string if missing)
 * @property {String} experimentID - the currently running Google Optimize experiment id (or `null` if no experiment is running)
 * @property {String} variationID - the currently running Google Optimize variation id (or `null` if no experiment is running)
 * @property {"mobile"|"desktop"} device - the current device type (`mobile` or `desktop`)
 * @property {"1"} swimlaneID - always hard-coded to `1`
 */
/**
 * @typedef RingpoolResponse
 * @property {String} rpid - the ringpool id
 * @property {String} tfn - the dynamic phone number returned by Ring Center
 */

import getGoogleIDs, {debugGoEvent} from '../tracking/getGoogleIDs';
import {isMTUser, isSemUser} from '../tracking/index';
import {getS7Value} from '../tracking/getSValues';
import isMobileScreen from '../isMobileScreen';
import {runAutoringpool} from '../ringpoolAuto';
import {runCustomNativeRingpool} from '../ringpoolCustomNative';
import {decodeNumber} from '../ringpoolCustomNative/helpers.js';

// this is our cache for ringpool responses, so we don't have to re-query the network API for the same ringpool ids and settings
// this cache is keyed with a string representation of the ringpool settings
const inMemoryCache = new Map();
const RINGPOOL_API_BASE = 'https://rs.cfringctr.com/api/ReserveCustomNativeNumbers';
// NOTE: We are currently hosting the autoringpool script locally.
//const AUTORINGPOOL_URL = 'https://d331h1l13ox5yq.cloudfront.net/scripts/autoringpool-seo-latest-v4.js';

function populateRingpoolSettings(customField, experimentId, variationId, customfield2 = '', typeid = 1)
{
    let passedExperimentId = experimentId === '' ? null : experimentId;
    let passedVariationId = variationId === '' ? null : variationId;
    return {
        'pageType': (customField ? customField.value : 'pagetype-unknown'),
        'experimentID': passedExperimentId,
        'variationID': passedVariationId,
        'device': (isMobileScreen() ? 'mobile' : 'desktop'),
        'swimlaneID': '1', // purposefully hard-coded, swimlane is passed to ringpool but not used by native reservation
        'hasUniquePhoneReservation': (customfield2 ? customfield2.value : '1'), // Ringpool Top 100 tracked cities / zips
        'typeid': typeid,
        's7': isMTUser() ? getS7Value() : null,
    };
}

/**
 * Retrieve parameters passed to native ringpool service
 * @returns {Promise<RingpoolSettings>}
 */
export function getRingpoolSettings(typeid = 1)
{
    debugGoEvent({
        'event': 'goExperimentRingpoolFetchStart',
    });

    return new Promise((resolve) => {
        const customField = document.getElementById('customfield1');
        const customField2 = document.getElementById('customfield2'); // Ringpool Top 100 tracked cities / zips

        getGoogleIDs('getRingpoolSettings')
            .then(googleIDs => {
                if (googleIDs.optimizeData === null) {
                    let ringpoolSettings = populateRingpoolSettings(customField, null, null, customField2, typeid);

                    return resolve(ringpoolSettings);
                }

                let ringpoolSettings = populateRingpoolSettings(
                    customField,
                    googleIDs.optimizeData.experimentId,
                    googleIDs.optimizeData.variationId,
                    customField2,
                    typeid,
                );
                debugGoEvent({
                    'event': 'goExperimentRingpoolSettingsCompleted', ringpoolSettings,
                });

                return resolve(ringpoolSettings);
            })
            .catch(error => {
                let ringpoolSettings = populateRingpoolSettings(customField, null, null, customField2, typeid);
                debugGoEvent({
                    'event': 'goExperimentRingpoolFetchError', error,
                });

                return resolve(ringpoolSettings);
            });
    });
}

/**
 * Fetch dynamic ringpool data from the Ring Center API, and cache the results in memory.
 * If a particular ringpool id has been cached, the Ring Center API will not be requested again for that ringpool id unless the ringpool settings change.
 * In effect, this means you can call this function multiple times and network requests will only go out when there is new data.
 * @see {link getSettingsForRingpool}
 * @param ringpools {[number]|number} - which ringpool ids to query for dynamic numbers
 * @returns {Promise<[RingpoolResponse]>}
 */
export function getNumberForRingpools(ringpools, typeid = 1)
{
    // convert input data from a variety of potential formats, but really we expect an array of integers
    if (typeof(ringpools) === 'number') {
        ringpools = [ringpools];
    } else if (typeof(ringpools) === 'string') {
        let ringpoolInt = parseInt(ringpools, 10);
        if (!isNaN(ringpoolInt)) {
            return getNumberForRingpools(ringpoolInt);
        } else {
            console.error(`Invalid ringpool id: ${ringpools}`);
        }
    } else if (!Array.isArray(ringpools)) {
        console.error(`Invalid ringpool id: ${ringpools}`);
    }

    return new Promise((resolve) => {
        return getRingpoolSettings(typeid)
            .then((ringpoolSettings) => {
                // create a stable string key for the current ringpool settings
                // this is required since Maps cannot use a plain Javascript object as a key
                const ringpoolSettingsKey = JSON.stringify(ringpoolSettings, Object.keys(ringpoolSettings).sort());
                if (!inMemoryCache.has(ringpoolSettingsKey)) {
                    // create an empty object for storing ringpool data, if this is a new set of ringpool settings
                    inMemoryCache.set(ringpoolSettingsKey, {});
                }

                // we divide our desired ringpools into cached and uncached
                // we only need to trigger a network request for uncached ringpools
                const cachedRingpoolMap = inMemoryCache.get(ringpoolSettingsKey);
                let cachedRingpoolIds = [];
                let uncachedRingpoolIds = [];
                ringpools.forEach(rpid => {
                    if (Object.prototype.hasOwnProperty.call(cachedRingpoolMap, rpid)) {
                        cachedRingpoolIds.push(rpid);
                    } else {
                        uncachedRingpoolIds.push(rpid);
                    }
                });

                // cached ringpool promise directly returns the ringpool data from the cache
                let cachedRingpoolPromise = new Promise((resolve) => {
                    return resolve(cachedRingpoolIds.map(rpid => {
                        return {
                            tfn: cachedRingpoolMap[rpid],
                            rpid: rpid,
                        }
                    }));
                });

                // uncached ringpool ids require a network request to Ring Center
                let networkRingpoolPromise = new Promise((resolve) => {
                    if (uncachedRingpoolIds.length === 0) {
                        // if we have none, return empty array rather than firing a network request with no ringpools
                        return resolve([]);
                    } else {
                        // TODO: forces ringpool to work by replicating the zip page passed parameters. delete after release and/or testing
                        // ringpoolSettings.pageType = 'pagetype-zip';
                        // ringpoolSettings.variationID = '0';
                        // ringpoolSettings.experimentID = 'zip';
                        // format the ringpools in the proper format for the API
                        // TODO: REMOVED FOR RELEASE. REINTRODUCE AFTER RELEASE
                        // const attr5Value = (ringpoolSettings.hasUniquePhoneReservation) ? ringpoolSettings.hasUniquePhoneReservation : ringpoolSettings.swimlaneID;
                        const formattedRingpools = `[${uncachedRingpoolIds.map(rp => `"${rp}"`).join(',')}]`;
                        let queryParams = new URLSearchParams({
                            ringPoolIDs: formattedRingpools,
                            typeid: ringpoolSettings.typeid,
                            attr1: ringpoolSettings.pageType,
                            attr2: ringpoolSettings.experimentID,
                            attr3: ringpoolSettings.variationID,
                            attr4: ringpoolSettings.device,
                            attr5: ringpoolSettings.swimlaneID,
                            //attr6: ringpoolSettings.s7 || null, // Add in later
                        });

                        const apiUrl = `${RINGPOOL_API_BASE}?${queryParams}`;
                        return fetch(apiUrl)
                            .then(response => response.json())
                            .then(data => {
                                return resolve(data.response.map(reservation => {
                                    // ringpool data is returned from Ring Center with encoded numbers and various other properties we don't care about
                                    // so we convert this into a format we can properly use (and which matches the cached number format)
                                    const reservationResult = {
                                        tfn: decodeNumber(reservation.number),
                                        rpid: reservation.ring_pool_id,
                                    };
                                    // Debugging:
                                    // console.debug('reservation response', reservationResult);

                                    return reservationResult
                                }));
                            });
                    }
                });
                // wait until the networked and cached ringpools have been processed before moving onto the next step
                // uncached ringpools should be near instantaneous but the network request will take some unknown time
                // we also pass the ringpool settings key which is needed for caching the newly-fetched ringpools
                return Promise.all([networkRingpoolPromise, cachedRingpoolPromise, new Promise(resolve => resolve(ringpoolSettingsKey))]);
            })
            .then(([networkedRingpools, cachedRingpools, ringpoolSettingsKey]) => {
                let currentlyCachedRingpools = inMemoryCache.get(ringpoolSettingsKey);
                let allReturnedRingpools = cachedRingpools.concat(networkedRingpools);
                let ringpoolObject = {};
                allReturnedRingpools.forEach(ringpool => {
                    ringpoolObject[ringpool.rpid] = ringpool.tfn;
                });

                // combine current cache with newly-fetched ringpool data to form a complete cache, and store it
                let newRingpoolCache = Object.assign(currentlyCachedRingpools, ringpoolObject);
                inMemoryCache.set(ringpoolSettingsKey, newRingpoolCache);

                return resolve(allReturnedRingpools);
            });
    });
}

// our default function automatically replaces all ringpools in the DOM with dynamic numbers
// this should be called on page load for every page
// however, if you are only importing this file for API-driven ringpools, you do not need to run this default function
export default () => {
    const isBot = navigator.userAgent.includes('AdsBot-Google')
        || navigator.userAgent.includes('bingbot.htm')
        || navigator.userAgent.includes('Yahoo Ad monitoring');

    if (isBot) {
        // if Agent is a bot, do not run the ringpool script
        return;
    }

    if (isSemUser()) {
        // go through the DOM and search for all elements with data-ringpool-alt-id data attribute
        const ringpoolNumbersSEM = document.querySelectorAll('[data-ringpool-alt-id]');
        if (ringpoolNumbersSEM?.length > 0) {
            runAutoringpool(ringpoolNumbersSEM);
        }
    } else {
        const ringpoolNumbers = Array.from(document.querySelectorAll('[data-ringpool-id]'));
        if (ringpoolNumbers?.length > 0) {
            runCustomNativeRingpool(ringpoolNumbers);
        }
    }
}
